import React, { useState, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  HardDrive, 
  ShoppingCart, 
  ArrowUpCircle, 
  ArrowDownCircle,
  Package
} from 'lucide-react';
import { useApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './DashboardCharts.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DashboardCharts = () => {
  const { getDashboardStats } = useApiService();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await getDashboardStats();
        if (isMounted) {
          setStats(data);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        if (isMounted) {
          const errorMessage = err.message || 'Failed to load dashboard statistics';
          setError(errorMessage);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format number with commas
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Memoize chart data to prevent unnecessary re-renders
  const chartData = useMemo(() => {
    if (!stats?.stats) return null;

    const isSuperAdmin = stats.role === 'superAdmin';
    const s = stats.stats;

    // Generate stats cards based on role
    let statCards = [];

    const accent = '#6366f1';
    const accentLight = '#f1f5ff';

    if (isSuperAdmin) {
      statCards = [
        {
          title: 'Total Storage Assigned',
          value: s.totalStorageAssignedFormatted || '0 MB',
          icon: HardDrive,
          bgColor: accentLight,
          iconColor: accent,
        },
        {
          title: 'Total Storage Used',
          value: s.totalStorageUsedFormatted || '0 MB',
          icon: HardDrive,
          bgColor: '#fef2f2',
          iconColor: '#dc2626',
        },
        {
          title: 'Total Users',
          value: formatNumber(s.totalUsers || 0),
          icon: Users,
          bgColor: '#f0f9ff',
          iconColor: '#0284c7',
        },
        {
          title: 'Total Deals',
          value: formatNumber(s.totalDeals || 0),
          icon: ShoppingCart,
          bgColor: '#f5f3ff',
          iconColor: '#8b5cf6',
        },
        {
          title: 'Deals Sold',
          value: formatNumber(s.dealsSold || 0),
          icon: ArrowUpCircle,
          bgColor: '#f5f3ff',
          iconColor: '#8b5cf6',
        },
        {
          title: 'Deals Unsold',
          value: formatNumber(s.dealsUnsold || 0),
          icon: ArrowDownCircle,
          bgColor: '#f1f5f9',
          iconColor: '#64748b',
        },
        {
          title: 'Total Deal Value',
          value: formatCurrency(s.totalDealValue || 0),
          icon: DollarSign,
          bgColor: accentLight,
          iconColor: accent,
        },
      ];
    } else {
      // Admin and User Dashboard
      statCards = [
      {
          title: 'Showroom Storage',
          value: s.storageAssignedFormatted || '0 MB',
          subtitle: `Used: ${s.storageUsedFormatted || '0 MB'}`,
          icon: HardDrive,
          bgColor: accentLight,
          iconColor: accent,
        },
        {
          title: 'Number of Users',
          value: formatNumber(s.numberOfUsers || 0),
          icon: Users,
          bgColor: '#f0f9ff',
          iconColor: '#0284c7',
        },
        {
          title: 'Total Deals',
          value: formatNumber(s.totalDeals || 0),
          icon: Package,
          bgColor: accentLight,
          iconColor: accent,
        },
        {
          title: 'Deals Sold',
          value: formatNumber(s.dealsSold || 0),
          icon: ArrowUpCircle,
          bgColor: '#f5f3ff',
          iconColor: '#8b5cf6',
        },
        {
          title: 'Deals Unsold',
          value: formatNumber(s.dealsUnsold || 0),
          icon: ArrowDownCircle,
          bgColor: '#f1f5f9',
          iconColor: '#64748b',
        },
        {
          title: 'Total Money to Give',
          value: formatCurrency(s.totalMoneyToGive || 0),
          icon: ArrowDownCircle,
          bgColor: '#fef2f2',
          iconColor: '#dc2626',
        },
        {
          title: 'Total Money to Receive',
          value: formatCurrency(s.totalMoneyToReceive || 0),
          icon: ArrowUpCircle,
          bgColor: '#f5f3ff',
          iconColor: '#8b5cf6',
        },
      ];
    }

    // Create simplified charts for better performance
    const dealDistributionData = {
      labels: ['Sold', 'Unsold'],
      datasets: [{
        data: [s.dealsSold || 0, s.dealsUnsold || 0],
        backgroundColor: [
          'rgba(139, 92, 246, 0.8)',
          'rgba(100, 116, 139, 0.8)',
        ],
        borderColor: [
          'rgb(139, 92, 246)',
          'rgb(100, 116, 139)',
        ],
        borderWidth: 2,
      }],
  };

    const valueDistributionData = {
      labels: ['Sold Deals Value', 'Unsold Deals Value'],
      datasets: [{
        data: [s.soldDealsValue || 0, s.unsoldDealsValue || 0],
        backgroundColor: [
          'rgba(139, 92, 246, 0.8)',
          'rgba(100, 116, 139, 0.8)',
        ],
        borderColor: [
          'rgb(139, 92, 246)',
          'rgb(100, 116, 139)',
        ],
        borderWidth: 2,
      }],
    };

    // Financial summary by date for admin/user
    let financialSummaryData = null;
    if (!isSuperAdmin && s.financialSummaryByDate && Array.isArray(s.financialSummaryByDate)) {
      const dates = s.financialSummaryByDate.map(item => {
        const date = new Date(item.date);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });
      
      financialSummaryData = {
        labels: dates,
        datasets: [
          {
            label: 'Purchase Value',
            data: s.financialSummaryByDate.map(item => item.purchaseValue || 0),
            borderColor: 'rgb(100, 116, 139)',
            backgroundColor: 'rgba(100, 116, 139, 0.1)',
            tension: 0.4,
            fill: true,
          },
          {
            label: 'Sale Value',
            data: s.financialSummaryByDate.map(item => item.saleValue || 0),
            borderColor: 'rgb(139, 92, 246)',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            tension: 0.4,
            fill: true,
          },
          {
            label: 'Profit',
            data: s.financialSummaryByDate.map(item => item.profit || 0),
            borderColor: 'rgb(99, 102, 241)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            tension: 0.4,
            fill: true,
      },
    ],
  };
    }

    return { statCards, dealDistributionData, valueDistributionData, financialSummaryData };
  }, [stats]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: '600',
        },
        bodyFont: {
          size: 13,
        },
        cornerRadius: 8,
      },
    },
  };

  const doughnutOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
    },
  };

  const lineOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          },
        },
      },
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
          <p style={{ color: '#6b7280', margin: 0 }}>Loading dashboard...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    const handleRetry = () => {
      setError(null);
      setLoading(true);
      getDashboardStats()
        .then((data) => {
          setStats(data);
          setError(null);
        })
        .catch((err) => {
          console.error('Error fetching dashboard stats:', err);
          setError(err.message || 'Failed to load dashboard statistics');
        })
        .finally(() => {
          setLoading(false);
        });
    };

    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#dc2626', margin: '0 0 1rem' }}>{error}</p>
          <button 
            onClick={handleRetry} 
            style={{ 
              background: '#6366f1', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '8px', 
              padding: '0.5rem 1rem', 
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!stats || !chartData) {
    return null;
  }

  const isSuperAdmin = stats.role === 'superAdmin';

  return (
    <div style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
      background: '#fafbfc', 
      minHeight: '100vh',
      padding: '2rem 1.5rem',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <div>
          <h1 className="fw-bold mb-2" style={{ 
            color: '#1f2937', 
            fontSize: '1.75rem', 
            letterSpacing: '-0.025em'
          }}>
            Dashboard
          </h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem', color: '#64748b' }}>
            {isSuperAdmin 
              ? 'Monitor all showrooms and system-wide metrics' 
              : 'Monitor your showroom metrics and performance'}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'flex', 
        gap: '1.5rem', 
        flexWrap: 'wrap', 
        marginBottom: '1.5rem', 
        width: '100%' 
      }}>
        {chartData.statCards.map((stat, index) => (
          <div key={index} style={{ flex: '1 1 180px', minWidth: 0 }}>
            <div className="card border-0 shadow-sm h-100" style={{ 
              background: '#fff', 
              borderRadius: '12px', 
              width: '100%'
            }}>
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ 
                    width: 40, 
                    height: 40, 
                    background: stat.bgColor || '#f1f5ff', 
                    borderRadius: '10px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    flexShrink: 0 
                  }}>
                    <stat.icon size={20} style={{ color: stat.iconColor || '#6366f1' }} />
                  </div>
                  <div style={{ marginLeft: 12, flex: 1, minWidth: 0 }}>
                    <h6 className="mb-1 fw-semibold" style={{ 
                      color: '#1f2937', 
                      fontSize: '0.875rem'
                    }}>
                      {stat.title}
                    </h6>
                    <p className="mb-0 fw-bold" style={{ 
                      color: '#1f2937', 
                      fontSize: '1.25rem', 
                      margin: 0
                    }}>
                      {stat.value}
                    </p>
                    {stat.subtitle && (
                      <p className="mb-0 text-muted" style={{ 
                        fontSize: '0.75rem',
                        marginTop: '4px'
                      }}>
                        {stat.subtitle}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div style={{ 
        display: 'flex', 
        gap: '1.5rem', 
        flexWrap: 'wrap', 
        width: '100%' 
      }}>
        {/* Deal Distribution Chart */}
        <div style={{ flex: '1 1 350px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm" style={{ 
            background: '#fff', 
            borderRadius: '12px', 
            width: '100%'
          }}>
            <div className="card-body p-3 p-md-4">
              <div style={{ marginBottom: '1rem' }}>
                <h6 className="fw-semibold mb-1" style={{ color: '#1f2937', fontSize: '1rem' }}>
                  Deal Distribution
                </h6>
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                  {isSuperAdmin ? 'Sold vs Unsold deals across all showrooms' : 'Sold vs Unsold deals'}
                </p>
              </div>
              <div style={{ height: '300px', position: 'relative' }}>
                <Doughnut data={chartData.dealDistributionData} options={doughnutOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Value Distribution Chart */}
        <div style={{ flex: '1 1 350px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm" style={{ 
            background: '#fff', 
            borderRadius: '12px', 
            width: '100%'
          }}>
            <div className="card-body p-3 p-md-4">
              <div style={{ marginBottom: '1rem' }}>
                <h6 className="fw-semibold mb-1" style={{ color: '#1f2937', fontSize: '1rem' }}>
                  Value Distribution
                </h6>
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                  Sold deals value vs Unsold deals value
                </p>
              </div>
              <div style={{ height: '300px', position: 'relative' }}>
                <Doughnut data={chartData.valueDistributionData} options={doughnutOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Financial Summary Chart (Admin/User only) */}
        {!isSuperAdmin && chartData.financialSummaryData && (
          <div style={{ flex: '1 1 100%', minWidth: 0 }}>
            <div className="card border-0 shadow-sm" style={{ 
              background: '#fff', 
              borderRadius: '12px', 
              width: '100%'
            }}>
              <div className="card-body p-3 p-md-4">
                <div style={{ marginBottom: '1rem' }}>
                  <h6 className="fw-semibold mb-1" style={{ color: '#1f2937', fontSize: '1rem' }}>
                    Financial Summary (Last 30 Days)
                  </h6>
                  <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    Daily purchase value, sale value, and profit trends
                  </p>
                </div>
                <div style={{ height: '400px', position: 'relative' }}>
                  <Line data={chartData.financialSummaryData} options={lineOptions} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardCharts; 
